import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { newToastEvent } from "../../utils/ToastUtil";
import LoadingView from "../SharedViewLayout/LoadingView";
import PasswordProtectedView from "../SharedViewLayout/PasswordProtectedView";
import SharedScreenAPI from "../../api/api/SharedScreenAPI";

import NotFoundView from "../SharedViewLayout/NotFoundView";
import { pagePasswordAtom } from "../SharedPageLayout/useSharedPage";
import useSharedDocument from "./useSharedDocument";
import PageViewDocument from "../../components/PageView/PageViewDocument/PageViewDocument";

export default function SharedDocumentLayout() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const setPagePasswords = useSetAtom(pagePasswordAtom);

  const client = useQueryClient();
  const navigate = useNavigate();

  const { sharedDocID, tenantID } = useParams();
  const { sharedDocument, documentLoading } = useSharedDocument(
    tenantID!,
    sharedDocID!
  );

  const success = sharedDocument?.success;
  const requiresPassword = sharedDocument?.requiresPassword;

  // Enforces Login Permissions for the shared document, if applied by settings
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      if (
        err?.config?.url?.includes("/getSharedDocument/") &&
        err?.response?.status === 400 &&
        err?.response?.data === "Bad Request"
      ) {
        navigate(`/login?r=${window.location.href}`);
        newToastEvent("error", "Please login to access that page.");
      }

      return Promise.reject(err);
    }
  );

  if (documentLoading) {
    return <LoadingView />;
  }

  if (!success && requiresPassword) {
    return (
      <PasswordProtectedView
        onSubmit={updatePassword}
        submitting={submitting}
        error={error}
      />
    );
  }

  if (!success) return <NotFoundView />;

  return (
    <>
      <div className="d-flex align-items-center border-bottom py-3 px-4">
        <h3 className="mb-0">{sharedDocument?.document?.Name}</h3>
        <div>{sharedDocument?.document?.Description}</div>
      </div>
      <PageViewDocument
        view={{} as any}
        page={{} as any}
        document={{
          id: sharedDocument?.document?._id,
          ModuleAPIName: "Documents",
        }}
        useToolbar={false}
      />
    </>
  );

  async function updatePassword(password: string) {
    if (!password) return;

    setError("");
    setSubmitting(true);

    setPagePasswords((prev) => ({
      ...prev,
      [`${tenantID}-${sharedDocID}`]: password,
    }));

    try {
      const result = await SharedScreenAPI.getSharedDoc(
        tenantID!,
        sharedDocID!,
        password
      );

      const data = result.data.data;

      if (data.success === false) {
        setError("Invalid password provided.");
      } else if (data.success === true) {
        client.setQueryData(["shared-document", sharedDocID], data);

        client.setQueryData(
          [tenantID, "record", "Documents", data.document?._id],
          data.document
        );
      }
    } catch {
      setError("An unexpected error");
    } finally {
      setSubmitting(false);
    }
  }
}
